import { useEffect, useRef } from "react";
import type { PlayerId } from "../api/types";
import type { FeedEntry, FeedKind, FeedPart } from "../logic/feed";
import type { Theme } from "../theme/pixel";
import { panelStyle, uiText } from "../theme/styles";

interface Props {
  entries: FeedEntry[];
  viewer: PlayerId;
  theme: Theme;
  /** `eid` is null for cards that have moved to a hidden zone — caller
   *  falls back to `def_id` for those. */
  onInspectCard?: (eid: number | null, defId: string, name: string) => void;
  /** When true, the panel renders without its own panelStyle border —
   *  used in compact mode where preview + log share one outer panel. */
  embedded?: boolean;
}

export function EventLog({ entries, viewer, theme, onInspectCard, embedded = false }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Snap-to-bottom only when the user was already pinned there, so
  // scrolling up to read history doesn't get yanked back on each event.
  const stickyBottomRef = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      // 4px slop for fractional-pixel rendering at non-integer zoom.
      stickyBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickyBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [entries.length]);

  return (
    <div
      style={{
        ...(embedded ? {} : panelStyle(theme)),
        flex: 1,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minHeight: 0,
      }}
    >
      <div style={uiText(theme, { size: 14, dim: true })}>{theme.ornament} Event Log</div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          padding: "6px 8px",
          fontFamily: theme.fontBody,
          fontSize: 13,
          lineHeight: 1.5,
          color: theme.ink,
          minHeight: 120,
        }}
      >
        {entries.length === 0 ? (
          <div style={{ color: theme.inkFaint, fontStyle: "italic" }}>No events yet.</div>
        ) : (
          entries.map((entry, i) => (
            <EntryRow
              key={i}
              entry={entry}
              viewer={viewer}
              theme={theme}
              onInspectCard={onInspectCard}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  viewer,
  theme,
  onInspectCard,
}: {
  entry: FeedEntry;
  viewer: PlayerId;
  theme: Theme;
  onInspectCard?: (eid: number | null, defId: string, name: string) => void;
}) {
  const style = entryStyle(entry.kind, theme);
  return (
    <div
      style={{
        borderBottom: `1px dashed ${theme.panelEdge}33`,
        padding: "2px 0",
        ...style,
      }}
    >
      {entry.parts.map((part, i) => (
        <PartSpan
          key={i}
          part={part}
          viewer={viewer}
          theme={theme}
          onInspectCard={onInspectCard}
        />
      ))}
    </div>
  );
}

function PartSpan({
  part,
  viewer,
  theme,
  onInspectCard,
}: {
  part: FeedPart;
  viewer: PlayerId;
  theme: Theme;
  onInspectCard?: (eid: number | null, defId: string, name: string) => void;
}) {
  if (part.type === "text") {
    return <span>{part.text}</span>;
  }
  if (part.type === "player") {
    return <span>{part.id === viewer ? "You" : "Opponent"}</span>;
  }
  // Masked face-down opponent traps come through with an empty `def_id`
  // and a generic name like "a trap" — still bold, but no click target.
  const clickable = onInspectCard != null && part.def_id.length > 0;
  return (
    <span
      // Marker for the click-outside-cards hook so a span click doesn't
      // immediately clear the inspect state it just set.
      data-card-tile={clickable ? "true" : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={
        clickable
          ? () => onInspectCard(part.eid, part.def_id, part.name)
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onInspectCard(part.eid, part.def_id, part.name);
              }
            }
          : undefined
      }
      style={{
        fontWeight: "bold",
        cursor: clickable ? "pointer" : "default",
        color: clickable ? theme.accent : theme.ink,
        textDecoration: clickable ? "underline dotted" : "none",
        textUnderlineOffset: 2,
      }}
    >
      {part.name}
    </span>
  );
}

function entryStyle(kind: FeedKind, theme: Theme): React.CSSProperties {
  switch (kind) {
    case "turn_banner":
    case "phase_banner":
      return { color: theme.inkFaint, fontStyle: "italic" };
    case "result":
      return { color: theme.accent, fontWeight: "bold" };
    case "fizzled":
      return { color: theme.inkDim, fontStyle: "italic" };
    case "trap_sprung":
    case "unsealed":
    case "seized":
    case "player_damaged":
    case "creature_damaged":
    case "died":
      return { color: theme.accent };
    case "notice":
      return { color: theme.accent, fontStyle: "italic" };
    default:
      return {};
  }
}
